import { z } from 'zod';

import { createBankingCircleTextSchema, createBicSchema, createIbanSchema } from './bank';

export type BeneficiaryBankFormSchemeId = 'sepa' | 'swift';
export type BeneficiaryBankRoutingCodeType = 'bic';
export type BeneficiaryBankRoutingCodeKey = BeneficiaryBankRoutingCodeType;
export type BeneficiaryBankAddressField = 'country' | 'city' | 'street';

export type BeneficiaryBankAddress = {
  country: string;
  city: string;
  street: string;
};

export type BeneficiaryBankFormConfig = {
  optionLabelKey: string;
  disabled: boolean;
  routingCodeLabelKey: string;
  routingCodePlaceholderKey: string;
  routingCodeType: BeneficiaryBankRoutingCodeType;
  showRoutingCodeField: boolean;
  requireRoutingCode: boolean;
  showAddressFields: boolean;
  requireAddressFields: boolean;
  addressFields: readonly BeneficiaryBankAddressField[];
  requiredAddressFields: readonly BeneficiaryBankAddressField[];
  showBankMetadataFields: boolean;
  requireBankMetadataFields: boolean;
};

const BENEFICIARY_BANK_FORM_VALIDATION_KEYS = {
  maxLength150: 'beneficiary_bank_form.validation.max_length_150',
  iban: 'beneficiary_bank_form.validation.iban',
  accountNumber: 'beneficiary_bank_form.validation.account_number',
  accountNumberNotAllowed: 'beneficiary_bank_form.validation.account_number_not_allowed',
  bic: 'beneficiary_bank_form.validation.bic',
  country: 'beneficiary_bank_form.validation.country',
  city: 'beneficiary_bank_form.validation.city',
  street: 'beneficiary_bank_form.validation.street',
  bankName: 'beneficiary_bank_form.validation.bank_name',
  bankAddress: 'beneficiary_bank_form.validation.bank_address',
} as const;

const BENEFICIARY_BANK_FORM_LABEL_KEYS = {
  sepa: 'beneficiary_bank_form.scheme.sepa',
  swift: 'beneficiary_bank_form.scheme.swift',
  bicLabel: 'beneficiary_bank_form.routing_code.bic.label',
  bicPlaceholder: 'beneficiary_bank_form.routing_code.bic.placeholder',
} as const;

const SWIFT_ADDRESS_FIELDS = [
  'country',
  'city',
  'street',
] as const satisfies readonly BeneficiaryBankAddressField[];
const REQUIRED_SWIFT_ADDRESS_FIELDS = [
  'country',
  'city',
  'street',
] as const satisfies readonly BeneficiaryBankAddressField[];

const SEPA_BENEFICIARY_BANK_FORM_CONFIG = {
  optionLabelKey: BENEFICIARY_BANK_FORM_LABEL_KEYS.sepa,
  disabled: false,
  routingCodeLabelKey: BENEFICIARY_BANK_FORM_LABEL_KEYS.bicLabel,
  routingCodePlaceholderKey: BENEFICIARY_BANK_FORM_LABEL_KEYS.bicPlaceholder,
  routingCodeType: 'bic',
  showRoutingCodeField: true,
  requireRoutingCode: true,
  showAddressFields: false,
  requireAddressFields: false,
  addressFields: [],
  requiredAddressFields: [],
  showBankMetadataFields: false,
  requireBankMetadataFields: false,
} satisfies BeneficiaryBankFormConfig;

const SWIFT_BENEFICIARY_BANK_FORM_CONFIG = {
  optionLabelKey: BENEFICIARY_BANK_FORM_LABEL_KEYS.swift,
  disabled: false,
  routingCodeLabelKey: BENEFICIARY_BANK_FORM_LABEL_KEYS.bicLabel,
  routingCodePlaceholderKey: BENEFICIARY_BANK_FORM_LABEL_KEYS.bicPlaceholder,
  routingCodeType: 'bic',
  showRoutingCodeField: true,
  requireRoutingCode: true,
  showAddressFields: true,
  requireAddressFields: true,
  addressFields: SWIFT_ADDRESS_FIELDS,
  requiredAddressFields: REQUIRED_SWIFT_ADDRESS_FIELDS,
  showBankMetadataFields: false,
  requireBankMetadataFields: false,
} satisfies BeneficiaryBankFormConfig;

const BENEFICIARY_BANK_FORM_CONFIG: Record<BeneficiaryBankFormSchemeId, BeneficiaryBankFormConfig> =
  {
    sepa: SEPA_BENEFICIARY_BANK_FORM_CONFIG,
    swift: SWIFT_BENEFICIARY_BANK_FORM_CONFIG,
  };

const BENEFICIARY_BANK_ROUTING_CODE_KEY_BY_FORM_SCHEME = {
  sepa: 'bic',
  swift: 'bic',
} satisfies Record<BeneficiaryBankFormSchemeId, BeneficiaryBankRoutingCodeKey>;

const BENEFICIARY_BANK_ROUTING_CODE_SCHEMA_BY_FORM_SCHEME = {
  sepa: (required: boolean) =>
    createBicSchema({
      required,
      errorMessage: BENEFICIARY_BANK_FORM_VALIDATION_KEYS.bic,
    }),
  swift: (required: boolean) =>
    createBicSchema({
      required,
      errorMessage: BENEFICIARY_BANK_FORM_VALIDATION_KEYS.bic,
    }),
} satisfies Record<BeneficiaryBankFormSchemeId, (required: boolean) => z.ZodType>;

const createOptionalTextSchema = () =>
  z.string().trim().max(150, BENEFICIARY_BANK_FORM_VALIDATION_KEYS.maxLength150);

const createAddressFieldSchema = (required: boolean, errorMessage: string) =>
  createBankingCircleTextSchema({ required, requiredErrorMessage: errorMessage });

const shouldRequireAddressField = (
  config: BeneficiaryBankFormConfig,
  field: BeneficiaryBankAddressField,
  addressFieldsRequired: boolean,
): boolean => addressFieldsRequired && config.requiredAddressFields.includes(field);

export const getBeneficiaryBankFormConfig = (
  scheme: BeneficiaryBankFormSchemeId,
): BeneficiaryBankFormConfig => BENEFICIARY_BANK_FORM_CONFIG[scheme];

export const toBeneficiaryBankRoutingCodes = (
  scheme: BeneficiaryBankFormSchemeId,
  routingCode: string | undefined,
): Record<string, string> => {
  if (routingCode === undefined || routingCode.length === 0) {
    return {};
  }

  return {
    [BENEFICIARY_BANK_ROUTING_CODE_KEY_BY_FORM_SCHEME[scheme]]: routingCode,
  };
};

export const createBeneficiaryBankRoutingCodeSchema = (
  scheme: BeneficiaryBankFormSchemeId,
  options?: { required?: boolean },
) => {
  const { required = true } = options ?? {};

  return BENEFICIARY_BANK_ROUTING_CODE_SCHEMA_BY_FORM_SCHEME[scheme](required);
};

export const createBeneficiaryBankDetailsSchema = (input: {
  scheme: BeneficiaryBankFormSchemeId;
  showRoutingCodeField?: boolean;
  routingCodeRequired?: boolean;
  routingCodeSchema?: z.ZodType<string> | z.ZodType<string | undefined>;
  addressFieldsRequired?: boolean;
  includeAddressFields?: boolean;
  includeBankName?: boolean;
  includeBankAddress?: boolean;
}) => {
  const config = getBeneficiaryBankFormConfig(input.scheme);
  const showRoutingCodeField = input.showRoutingCodeField ?? config.showRoutingCodeField;
  const routingCodeRequired =
    showRoutingCodeField && (input.routingCodeRequired ?? config.requireRoutingCode);
  const addressFieldsRequired =
    config.showAddressFields && (input.addressFieldsRequired ?? config.requireAddressFields);

  return z
    .object({
      iban: createIbanSchema({
        required: input.scheme === 'sepa',
        errorMessage: BENEFICIARY_BANK_FORM_VALIDATION_KEYS.iban,
      }),
      accountNumber: createOptionalTextSchema().optional(),
      routingCode: showRoutingCodeField
        ? (input.routingCodeSchema ??
          createBeneficiaryBankRoutingCodeSchema(input.scheme, { required: routingCodeRequired }))
        : z.string().optional(),
      country:
        input.includeAddressFields === false
          ? z.string().optional()
          : createAddressFieldSchema(
              shouldRequireAddressField(config, 'country', addressFieldsRequired),
              BENEFICIARY_BANK_FORM_VALIDATION_KEYS.country,
            ),
      city:
        input.includeAddressFields === false
          ? z.string().optional()
          : createAddressFieldSchema(
              shouldRequireAddressField(config, 'city', addressFieldsRequired),
              BENEFICIARY_BANK_FORM_VALIDATION_KEYS.city,
            ),
      street:
        input.includeAddressFields === false
          ? z.string().optional()
          : createAddressFieldSchema(
              shouldRequireAddressField(config, 'street', addressFieldsRequired),
              BENEFICIARY_BANK_FORM_VALIDATION_KEYS.street,
            ),
      bankName:
        input.includeBankName === false
          ? z.string().optional()
          : config.requireBankMetadataFields
            ? z
                .string()
                .trim()
                .min(1, BENEFICIARY_BANK_FORM_VALIDATION_KEYS.bankName)
                .max(150, BENEFICIARY_BANK_FORM_VALIDATION_KEYS.maxLength150)
            : createOptionalTextSchema(),
      bankAddress:
        input.includeBankAddress === false
          ? z.string().optional()
          : config.requireBankMetadataFields
            ? z
                .string()
                .trim()
                .min(1, BENEFICIARY_BANK_FORM_VALIDATION_KEYS.bankAddress)
                .max(150, BENEFICIARY_BANK_FORM_VALIDATION_KEYS.maxLength150)
            : createOptionalTextSchema(),
    })
    .superRefine((bankDetails, context) => {
      if (input.scheme === 'sepa' && (bankDetails.accountNumber?.length ?? 0) > 0) {
        context.addIssue({
          code: 'custom',
          path: ['accountNumber'],
          message: BENEFICIARY_BANK_FORM_VALIDATION_KEYS.accountNumberNotAllowed,
        });
        return;
      }

      if (
        input.scheme === 'swift' &&
        bankDetails.iban.length === 0 &&
        (bankDetails.accountNumber?.length ?? 0) === 0
      ) {
        context.addIssue({
          code: 'custom',
          path: ['accountNumber'],
          message: BENEFICIARY_BANK_FORM_VALIDATION_KEYS.accountNumber,
        });
      }
    });
};
